import React from 'react';
import styled from 'styled-components';
import { colors, typography } from '../../../styles/theme';
import { ContentSection, SectionTitle, Tag, EmptyState } from '../common/StyledComponents';

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
`;

const Th = styled.th`
  text-align: left;
  padding: 24px 12px;
  font-family: ${typography.fontFamily};
  font-weight: ${typography.fontWeight.medium};
  color: ${colors.text.secondary};
  border-bottom: 1px solid ${colors.border};
  width: ${props => props.width || 'auto'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Td = styled.td`
  padding: 24px 12px;
  font-family: ${typography.fontFamily};
  color: ${colors.text.primary};
  border-bottom: 1px solid ${colors.border};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TableRow = styled.tr`
  transition: background-color 0.2s ease;
  cursor: ${props => props.clickable ? 'pointer' : 'default'};

  &:hover {
    background-color: ${props => props.clickable ? 'rgba(0, 0, 0, 0.05)' : 'transparent'};
  }
`;

const Amount = styled.span`
  font-family: ${typography.fontFamily};
  font-weight: ${typography.fontWeight.medium};
`;

const DataTable = ({ 
  title, 
  columns, 
  data, 
  onRowClick,
  renderCell,
  emptyState = "No data available" 
}) => {
  if (!data || data.length === 0) {
    return (
      <ContentSection>
        <SectionTitle>{title}</SectionTitle>
        <EmptyState>{emptyState}</EmptyState>
      </ContentSection>
    );
  }

  return (
    <ContentSection>
      <SectionTitle>{title}</SectionTitle>
      <Table>
        <thead>
          <tr>
            {columns.map((column, index) => (
              <Th key={index} width={column.width}>
                {column.label}
              </Th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, rowIndex) => (
            <TableRow 
              key={rowIndex}
              onClick={() => onRowClick && onRowClick(row, rowIndex)}
              clickable={!!onRowClick}
            >
              {columns.map((column, colIndex) => (
                <Td key={colIndex}>
                  {renderCell ? 
                    renderCell(column.key, row[column.key], row) :
                    row[column.key]}
                </Td>
              ))}
            </TableRow>
          ))}
        </tbody>
      </Table>
    </ContentSection>
  );
};

// Helper components for common cell renderers
export const TagCell = ({ value, getTagProps }) => {
  const props = getTagProps ? getTagProps(value) : {};
  return <Tag {...props}>{value}</Tag>;
};

export const AmountCell = ({ value, currency = '£' }) => (
  <Amount>{currency}{value}</Amount>
);

export const DateCell = ({ value, format = 'en-GB' }) => (
  <>{new Date(value).toLocaleDateString(format)}</>
);

export default DataTable; 